import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraDataTable } from '@mono/ui-components/captains';
import { SpecraSwatch, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraEmptyState, SpecraSegmentedControl, SpecraSwitchField } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { currentYear, yearOf } from './model';
import { orgTone } from './orgTone';
const T = Testids.peopleMovement;
export const sinceYearOf = (span)=>span === 'all' ? undefined : Number(span.slice('since-'.length));
function OrgCell({ org, text }) {
    if (org?.id) {
        return /*#__PURE__*/ _jsxs("span", {
            className: "inline-flex items-center gap-[var(--space-1-5)]",
            children: [
                /*#__PURE__*/ _jsx(SpecraSwatch, {
                    shape: "square",
                    tone: orgTone(org)
                }),
                org.name
            ]
        });
    }
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: text || '—'
    });
}
const yearLabel = (m)=>{
    const y = yearOf(m.movedOn);
    return y === undefined ? 'Undated' : m.startInferred ? `${y} (inferred)` : String(y);
};
/** Movement between organizations as a moves table, plus a block for undated moves. */ export function MovesView({ data, span, onSpanChange, acquisitions, onAcquisitionsChange, onSelectPerson }) {
    const thisYear = currentYear();
    const dated = data.moves.filter((m)=>!m.undated);
    const undated = data.moves.filter((m)=>m.undated);
    const columns = [
        {
            id: 'person',
            header: 'Person',
            cell: (m)=>m.headcount > 1 ? `${m.contactName} (${m.headcount})` : m.contactName
        },
        {
            id: 'from',
            header: 'From',
            cell: (m)=>/*#__PURE__*/ _jsx(OrgCell, {
                    org: m.from
                })
        },
        {
            id: 'to',
            header: 'To',
            cell: (m)=>/*#__PURE__*/ _jsx(OrgCell, {
                    org: m.to,
                    text: m.toText
                })
        },
        {
            id: 'year',
            header: 'Year',
            cell: yearLabel
        }
    ];
    const onRowClick = (m)=>{
        if (m.contactId) onSelectPerson(m.contactId);
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-[var(--space-4)]",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-[var(--space-4)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                        label: "Span",
                        size: "sm",
                        testId: T.spanFilter,
                        value: span,
                        options: [
                            {
                                value: 'all',
                                label: 'All time'
                            },
                            {
                                value: `since-${thisYear - 2}`,
                                label: `Since ${thisYear - 2}`
                            }
                        ],
                        onValueChange: onSpanChange
                    }),
                    /*#__PURE__*/ _jsx(SpecraSwitchField, {
                        label: "Show acquisitions",
                        controlProps: {
                            checked: acquisitions,
                            onCheckedChange: onAcquisitionsChange
                        },
                        "data-testid": T.acquisitionsToggle
                    })
                ]
            }),
            dated.length === 0 ? /*#__PURE__*/ _jsx("div", {
                "data-testid": T.emptyMoves,
                children: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                    framed: true,
                    title: "No moves recorded in this span."
                })
            }) : /*#__PURE__*/ _jsx("div", {
                "data-testid": T.movesTable,
                children: /*#__PURE__*/ _jsx(SpecraDataTable, {
                    caption: "Movement between organizations",
                    columns: columns,
                    rows: dated,
                    framed: true,
                    rowKey: (m)=>m.id,
                    rowTestId: ()=>T.movesRow,
                    getRowLabel: (m)=>`${m.contactName}, ${yearLabel(m)}`,
                    onRowClick: onRowClick
                })
            }),
            undated.length > 0 && /*#__PURE__*/ _jsxs("section", {
                "aria-label": "Undated moves",
                className: "space-y-[var(--space-2)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        children: "Undated moves: a date is missing, so these are not placed on the timeline."
                    }),
                    /*#__PURE__*/ _jsx(SpecraDataTable, {
                        caption: "Undated moves",
                        columns: columns,
                        rows: undated,
                        framed: true,
                        rowKey: (m)=>m.id,
                        getRowLabel: (m)=>m.contactName,
                        onRowClick: onRowClick
                    })
                ]
            })
        ]
    });
}
